/**
 * CR150: Published Layout Info Block
 * 
 * Shows active preset info for published environments with sync action.
 */

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ExternalLink, FileJson, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { LayoutPreset } from "@/lib/layout-studio/types";

interface PublishedLayoutInfoProps {
  environment: "staging" | "publish";
  preset: LayoutPreset | null;
  lastSynced: string | null;
  syncing: boolean;
  onSync: () => void;
  onOpenLayoutStudio?: () => void;
  onViewJson?: () => void;
}

export function PublishedLayoutInfo({
  environment,
  preset,
  lastSynced,
  syncing,
  onSync,
  onOpenLayoutStudio,
  onViewJson,
}: PublishedLayoutInfoProps) {
  const envLabel = environment === "staging" ? "Staging" : "Live";

  if (!preset) {
    // Empty state
    return (
      <Card className="border-dashed">
        <CardContent className="py-3 px-4">
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">
                No active preset for <strong>{envLabel}</strong>. Activate one in Layout Studio.
              </p>
            </div>
            {onOpenLayoutStudio && (
              <Button variant="outline" size="sm" onClick={onOpenLayoutStudio} className="text-xs h-7">
                Go to Activate
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="py-3 px-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                Published layout
              </span>
              <Badge variant={environment === "publish" ? "default" : "secondary"} className="text-[9px] px-1.5">
                {envLabel}
              </Badge>
            </div>
            
            <div className="text-sm font-medium truncate" title={preset.name}>
              {preset.name}
            </div>
            
            {lastSynced && (
              <div className="text-[10px] text-muted-foreground">
                Last synced: {formatDistanceToNow(new Date(lastSynced), { addSuffix: true })}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onSync} 
              disabled={syncing}
              className="h-7 px-2 text-xs gap-1"
            >
              {syncing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <RefreshCw className="h-3 w-3" />
              )}
              Sync layout
            </Button>
            
            {onViewJson && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onViewJson}
                className="h-7 w-7 p-0"
                title="View preset JSON"
              >
                <FileJson className="h-3.5 w-3.5" />
              </Button>
            )}
            
            {onOpenLayoutStudio && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onOpenLayoutStudio}
                className="h-7 w-7 p-0"
                title="Open Layout Studio"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
